import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Stack,
  Chip,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Receipt as ReceiptIcon,
  Home as HomeIcon,
  List as OrdersIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);

  // Extract parameters from URL
  const status = searchParams.get('status');
  const invoiceId = searchParams.get('invoice_id');
  const transactionId = searchParams.get('transaction_id');
  const amount = searchParams.get('amount');

  useEffect(() => {
    // Simulate checking payment status
    const checkPaymentStatus = async () => {
      try {
        setLoading(true);
        
        // In a real app, you'd call your backend to verify the payment
        // For now, determine status from URL parameters
        
        if (status === 'success' || status === 'completed') {
          setPaymentStatus({
            success: true,
            invoiceId,
            transactionId,
            amount: amount ? parseFloat(amount) : null,
            message: 'Payment completed successfully!'
          });
        } else if (status === 'cancelled' || status === 'canceled') {
          setPaymentStatus({
            success: false,
            cancelled: true,
            message: 'Payment was cancelled'
          });
        } else if (status === 'failed' || status === 'error') {
          setPaymentStatus({
            success: false,
            failed: true,
            message: 'Payment failed. Please try again.'
          });
        } else {
          // Default to success if no specific status (Hyfin redirected back)
          setPaymentStatus({
            success: true,
            invoiceId,
            transactionId,
            amount: amount ? parseFloat(amount) : null,
            message: 'Payment processed successfully!'
          });
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
        setError('Unable to verify payment status');
      } finally {
        setLoading(false);
      }
    };

    checkPaymentStatus();
  }, [status, invoiceId, transactionId, amount]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: 400,
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Verifying payment status...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/client/order-history')}
          startIcon={<OrdersIcon />}
        >
          Back to Orders
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, px: 2 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          {paymentStatus?.success ? (
            <>
              <SuccessIcon sx={{ fontSize: 80, color: '#10b981', mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#10b981', mb: 2 }}>
                Payment Successful!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {paymentStatus.message}
              </Typography>
              
              {/* Payment Details */}
              <Box sx={{ 
                bgcolor: '#f8f9fa', 
                borderRadius: 2, 
                p: 3, 
                textAlign: 'left',
                mb: 3
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Payment Details
                </Typography>
                <Stack spacing={1}>
                  {paymentStatus.invoiceId && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Invoice ID:
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {paymentStatus.invoiceId}
                      </Typography>
                    </Box>
                  )}
                  {paymentStatus.transactionId && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Transaction ID:
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {paymentStatus.transactionId}
                      </Typography>
                    </Box>
                  )}
                  {paymentStatus.amount && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Amount Paid:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ${paymentStatus.amount.toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Payment Date:
                    </Typography>
                    <Typography variant="body2">
                      {new Date().toLocaleDateString()}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2">
                  <strong>Next Steps:</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • A receipt has been sent to your email
                  • Your order will be processed shortly
                  • You'll receive tracking information once shipped
                </Typography>
              </Alert>
            </>
          ) : paymentStatus?.cancelled ? (
            <>
              <ErrorIcon sx={{ fontSize: 80, color: '#f59e0b', mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b', mb: 2 }}>
                Payment Cancelled
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {paymentStatus.message}
              </Typography>
              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2">
                  No charges were made to your account. You can try paying again anytime.
                </Typography>
              </Alert>
            </>
          ) : (
            <>
              <ErrorIcon sx={{ fontSize: 80, color: '#ef4444', mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#ef4444', mb: 2 }}>
                Payment Failed
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {paymentStatus?.message || 'There was an issue processing your payment.'}
              </Typography>
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2">
                  Please check your payment method and try again. If the problem persists, contact support.
                </Typography>
              </Alert>
            </>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Action Buttons */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
            <Button
              variant="contained"
              onClick={() => navigate('/client/order-history')}
              startIcon={<OrdersIcon />}
              fullWidth
            >
              View Order History
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/client/dashboard')}
              startIcon={<HomeIcon />}
              fullWidth
            >
              Back to Dashboard
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Development Info */}
      {process.env.NODE_ENV === 'development' && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Development Mode - URL Parameters:</strong>
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', mt: 1 }}>
            status: {status || 'none'}<br/>
            invoice_id: {invoiceId || 'none'}<br/>
            transaction_id: {transactionId || 'none'}<br/>
            amount: {amount || 'none'}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default PaymentSuccess;
